import * as mqtt from "mqtt";
import { createContext, useEffect, useState } from "react";

import { SIMULATE, MQTT_BROKER_OVERRIDE } from "utils/constants";
import { useStore } from "hooks/use-store";
import { isNumeric } from "utils/validators";
import { DEV_SERVERS, SERVERS, serverFromUrl } from "./servers";
import { TopicHandler } from "./topics";

export const MqttClientContext = createContext<mqtt.MqttClient | null>(null);

export const MqttClientProvider = ({ children }: { children: any }) => {
  const [client, setClient] = useState<mqtt.MqttClient | null>(null);
  const store = useStore();

  /**
   * Handles incoming MQTT messages.
   *
   * @param topic - The topic of the incoming message.
   * @param message - The content of the incoming message.
   */
  const handleMessage = (topic: string, message: string) => {
    // Quick fix for handling topics with wildcards
    const split = topic.split("/");
    const hasWildCard = split.length > 1 && isNumeric(split[1]);

    const selectedTopicHandler =
      TopicHandler[hasWildCard ? topic.split("/")[0] : topic];

    if (selectedTopicHandler) {
      try {
        const formattedValue = selectedTopicHandler.format
          ? selectedTopicHandler.format(message)
          : message;

        selectedTopicHandler.set(store, formattedValue);
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    const servers = SIMULATE
      ? DEV_SERVERS
      : MQTT_BROKER_OVERRIDE
      ? [serverFromUrl(MQTT_BROKER_OVERRIDE)]
      : SERVERS;

    const client = mqtt.connect({ servers: servers });

    if (client) {
      client.on("connect", () => {
        setClient(client);

        console.info("MQTT Client:", client.options.hostname);

        // Subscribe to all frontend topics
        client.subscribe("frontend/#", { qos: 0 });

        // Subscribe to other necessary topics
        client.subscribe("trip_id", { qos: 0 });
        client.subscribe("weather_all", { qos: 0 });
        client.subscribe("eco_sog", { qos: 0 });
        client.subscribe("whole_body_vibration/#", { qos: 0 });
        client.subscribe("trip_captain", { qos: 0 });
        client.subscribe("position", { qos: 0 });

        client.on("message", (topic, message) => {
          handleMessage(topic, message.toString());
        });
      });

      client.on("error", (error) => {
        console.error("MQTT Connection Error:", error);
      });
    }

    return () => {
      client.end(true);
    };
  }, []);

  return (
    <MqttClientContext.Provider value={client}>
      {children}
    </MqttClientContext.Provider>
  );
};
